import { Box, Text, TextInput, RadioButton, CheckBox, Skeleton, FormField } from 'grommet';
import React, { useEffect, useState, useContext } from 'react';
import useSWR from 'swr';
import { NotificationsContext } from './NotificationsProvider';
import { handleErrors } from '../utils/errors';
import {
    faMagnifyingGlass as IconSearch,
    faUserGroup as IconGroup
} from '@fortawesome/pro-light-svg-icons';
import FontAwesomeIcon from './FontAwesomeIcon';
import Button from './Button';
import Badge from './Badge';
import { formatName } from '../utils/names';
import { formatAddress } from '../utils/addresses';

const CustomerRowSkeleton = () => (
    <Box
        direction="row"
        align="start"
        justify="between"
        pad={{ vertical: 'small', horizontal: 'small' }}
        border={{ side: 'bottom', color: 'border-weak' }}
        fill="horizontal"
    >
        <Box flex gap="xsmall">
            <Skeleton width="medium" height="14px" />
            <Skeleton width="small" height="12px" />
            <Skeleton width="200px" height="12px" />
        </Box>
        <Box flex={false} justify="center" pad={{ left: 'small' }}>
            <Skeleton width="18px" height="18px" round="full" />
        </Box>
    </Box>
);

const CustomerRow = ({ item, isSelected, onSelect, multiple, compact }) => {
    const isGroup = item._type === 'group';
    const customerName = item.company_name || formatName(item, true);
    const contactName = !isGroup && item.company_name ? formatName(item, true) : null;
    const billingAddress = item.billing_address || item.addresses?.[0];

    return (
        <Button
            event={{ section: 'customer_search', element: 'customer_row', description: item?.id }}
            fill="horizontal"
            hoverIndicator
            onClick={() => onSelect(item)}
        >
            <Box
                direction="row"
                align="center"
                gap="small"
                pad={compact ? { vertical: 'xsmall', horizontal: 'xsmall' } : { vertical: 'small', horizontal: 'small' }}
                border={isSelected ? { color: 'brand', size: 'small' } : { side: 'bottom', color: 'border-weak' }}
                round={isSelected ? 'xsmall' : undefined}
                background={isSelected ? 'background-contrast' : undefined}
                fill="horizontal"
            >
                <Box flex={false}>
                    {multiple ? (
                        <CheckBox
                            checked={isSelected}
                            onChange={() => {}}
                        />
                    ) : (
                        <RadioButton
                            name="customer"
                            checked={isSelected}
                            onChange={() => {}}
                        />
                    )}
                </Box>
                <Box flex>
                    <Text>{customerName}</Text>
                    {contactName && <Text size="xsmall" color="text-weak">{contactName}</Text>}
                    {!compact && billingAddress && formatAddress(billingAddress, false, true) && (
                        <Text size="xsmall" color="text-weak">
                            {formatAddress(billingAddress, false, true)}
                        </Text>
                    )}
                </Box>
                {isGroup && (
                    <Badge icon={IconGroup}>Group</Badge>
                )}
            </Box>
        </Button>
    );
};

const CustomerSearch = ({
    initialSelectedCustomer,
    initialSelectedCustomers,
    onSelect,
    excludeCustomerId,
    customerGroupId,
    multiple = false,
    includeGroups = false,
    minHeight = '300px',
    // Callers that give the search a whole drawer want the results to use the
    // room. The default keeps every existing caller exactly as it was.
    maxHeight = 'medium',
    // Draws one container around the search and its results instead of leaving
    // the input looking detached from the list. Off by default so existing
    // callers are untouched.
    framed = false,
    compact = false
}) => {
    const notificationCtx = useContext(NotificationsContext);
    const fetcher = (url) => fetch(url)
        .then(handleErrors)
        .catch(() => notificationCtx.showNotification({ message: "We weren't able to fetch data. Please try again.", type: 'error' }));

    const [searchKeyword, setSearchKeyword] = useState('');
    const [debouncedSearchKeyword, setDebouncedSearchKeyword] = useState('');
    // Single select state
    const [selectedCustomer, setSelectedCustomer] = useState(initialSelectedCustomer);
    // Multi select state
    const [selectedCustomers, setSelectedCustomers] = useState(initialSelectedCustomers || []);

    // Fetch customers
    const { data: customerData, isLoading: customersLoading } = useSWR(
        `/api/customers?limit=15${debouncedSearchKeyword ? `&keyword=${encodeURIComponent(debouncedSearchKeyword)}` : ''}${customerGroupId ? `&customer_group_id=${customerGroupId}` : ''}`,
        fetcher,
        { revalidateOnFocus: false }
    );

    // Fetch groups (only if includeGroups is true and not scoped to a specific group)
    const { data: groupData, isLoading: groupsLoading } = useSWR(
        includeGroups && !customerGroupId
            ? `/api/customers/groups?limit=10${debouncedSearchKeyword ? `&keyword=${encodeURIComponent(debouncedSearchKeyword)}` : ''}`
            : null,
        fetcher,
        { revalidateOnFocus: false }
    );

    const isLoading = customersLoading || (includeGroups && groupsLoading);

    // Combine customers and groups with type markers
    const items = [
        ...(groupData?.customer_groups || []).map(g => ({ ...g, _type: 'group' })),
        ...(customerData?.customers || []).map(c => ({ ...c, _type: 'customer' }))
    ];

    const totalCount = (customerData?.total || 0) + (groupData?.total || 0);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            setDebouncedSearchKeyword(searchKeyword);
        }, 300);

        return () => clearTimeout(delayDebounceFn);
    }, [searchKeyword]);

    const handleSelect = (item) => {
        if (multiple) {
            // Toggle selection in multi-select mode
            // Use both id and type for unique identification
            const isAlreadySelected = selectedCustomers.some(
                c => c.id === item.id && c._type === item._type
            );
            let newSelected;
            if (isAlreadySelected) {
                newSelected = selectedCustomers.filter(
                    c => !(c.id === item.id && c._type === item._type)
                );
            } else {
                newSelected = [...selectedCustomers, item];
            }
            setSelectedCustomers(newSelected);
            onSelect && onSelect(newSelected);
        } else {
            // Single select mode
            setSelectedCustomer(item);
            onSelect && onSelect(item);
        }
    };

    const isSelected = (item) => {
        if (multiple) {
            return selectedCustomers.some(
                c => c.id === item.id && c._type === item._type
            );
        }
        return selectedCustomer?.id === item.id && selectedCustomer?._type === item._type;
    };

    const getPlaceholder = () => {
        if (includeGroups) {
            return totalCount ? `Search ${totalCount} customers & groups...` : "Search customers & groups...";
        }
        if (customerGroupId) {
            const count = customerData?.customers?.length || 0;
            return count ? `Search ${count} group members...` : "Search group members...";
        }
        return customerData?.total ? `Search ${customerData.total} customers...` : "Search customers...";
    };

    // Two presentations. Unframed (the default) is what ChangeCustomerModal and
    // the search filter have always rendered: a bordered search input with a
    // separate list beneath it. Framed draws one rounded container around both,
    // for callers that give the search a surface of its own - without it the
    // input reads as floating outside the list it belongs to.
    const searchInput = compact ? (
        <TextInput
            type="search"
            size="small"
            icon={<FontAwesomeIcon SelectedIcon={IconSearch} color="text-xweak" size="xsmall" />}
            placeholder={getPlaceholder()}
            value={searchKeyword}
            onChange={(event) => setSearchKeyword(event.target.value)}
        />
    ) : framed ? (
        // Inside the frame the container already draws the border, so the input
        // is plain. A FormField here would box it a second time, which is the
        // seam that made the search look detached from its own results.
        <TextInput
            type="search"
            plain
            icon={<FontAwesomeIcon SelectedIcon={IconSearch} color="text-xweak" />}
            placeholder={getPlaceholder()}
            value={searchKeyword}
            onChange={(event) => setSearchKeyword(event.target.value)}
        />
    ) : (
        <FormField margin="none">
            <TextInput
                type="search"
                icon={<FontAwesomeIcon SelectedIcon={IconSearch} color="text-xweak" />}
                placeholder={getPlaceholder()}
                value={searchKeyword}
                onChange={(event) => setSearchKeyword(event.target.value)}
            />
        </FormField>
    );

    const results = (
        <Box height={{ min: minHeight, max: maxHeight }} overflow={{ horizontal: 'hidden', vertical: isLoading ? 'hidden' : 'auto' }}>
            {isLoading ? (
                <Box>
                    <CustomerRowSkeleton />
                    <CustomerRowSkeleton />
                    <CustomerRowSkeleton />
                    <CustomerRowSkeleton />
                </Box>
            ) : items.length === 0 ? (
                <Box pad="small">
                    <Text color="text-weak">
                        {includeGroups ? 'No customers or groups found.' : 'No customers found.'}
                    </Text>
                </Box>
            ) : (
                items
                    .filter(c => c.id !== excludeCustomerId)
                    .map((item) => (
                        <CustomerRow
                            key={`${item._type}-${item.id}`}
                            item={item}
                            isSelected={isSelected(item)}
                            onSelect={handleSelect}
                            multiple={multiple}
                            compact={compact}
                        />
                    ))
            )}
        </Box>
    );

    if (framed) {
        return (
            <Box border round="xsmall" overflow="hidden">
                {/* flex={false} is load-bearing. The results below ask for
                    min-height 300px, and in a height-constrained column this row
                    would otherwise shrink to nothing and let its own text spill
                    over the first result. */}
                <Box
                    flex={false}
                    border={{ side: 'bottom', color: 'border' }}
                    pad={{ horizontal: 'xsmall', vertical: 'xxsmall' }}>
                    {searchInput}
                </Box>
                {results}
            </Box>
        );
    }

    return (
        <Box gap="small">
            {searchInput}
            {results}
        </Box>
    );
};

export default CustomerSearch;
